import React from 'react'
import styled, { css } from 'styled-components'
import { pxToRem } from '../../utils'
import { up } from 'styled-breakpoints'
import DotLoader from '../DotLoader'

const ButtonBase = css`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  position: relative;
  font-size: ${pxToRem(14)};
  line-height: ${pxToRem(20)};
  ${up('md')} {
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(24)};
  }
`
const getBackgroundColor = props => {
  if (props.primary) return props.theme.colors.primary
  if (props.secondary) return 'transparent'
  if (props.tertiary) return 'none'
}

const getTextColor = props => {
  if (props.primary) return props.theme.palette.white
  else return props.theme.colors.primary
}

const getBorderColor = props => {
  const borderColor = props.secondary ? props.theme.palette.grey4 : 'transparent'
  return `1px solid ${borderColor}`
}

const getHoverStyle = props => {
  if (props.primary)
    return css`
      background: linear-gradient(0deg, ${getBlackOpaqueVariation(0.1)}, ${getBlackOpaqueVariation(0.1)}),
        ${getBackgroundColor};
      box-shadow: 0 2px 16px rgba(237, 17, 100, 0.2);
    `
  if (props.secondary) return `background: none;`

  if (props.tertiary) return `background: rgba(237, 17, 100, 0.1);`
}

const getActiveFocusStyle = props => {
  if (props.primary)
    return css`
      background: linear-gradient(0deg, ${getBlackOpaqueVariation(0.2)}, ${getBlackOpaqueVariation(0.2)}),
        ${getBackgroundColor};
      box-shadow: 0px 2px 16px rgba(237, 17, 100, 0.4);
    `
  if (props.secondary)
    return `background: #fce6ee;
  box-shadow: 0px 2px 16px rgba(237, 17, 100, 0.4);
  border: 1px solid ${props.theme.colors.primary};
  `
  if (props.tertiary) return `background: rgba(237, 17, 100, 0.2);`
}

const getDisabledStyle = props => {
  if (!props.loading) {
    if (props.primary)
      return css`
        background: ${props.theme.palette.grey6};
      `
    if (props.secondary)
      return css`
        color: ${props.theme.palette.grey3};
        background: ${props.theme.palette.grey5};
        border: 1px solid ${props.theme.palette.grey4};
      `
    if (props.tertiary)
      return css`
        color: ${props.theme.palette.grey4};
      `
  }
}

const getTextTransform = props => {
  if (props.tertiary) return `uppercase`
  else return `initial`
}

const getPadding = props => {
  if (props.size === 'small')
    return css`
      padding: 8px 12px;
      ${up('md')} {
        padding: 10px 20px;
      }
    `
  else
    return css`
      padding: ${pxToRem(5)} ${pxToRem(20)};
      ${up('md')} {
        padding: ${pxToRem(7)} ${pxToRem(20)};
      }
    `
}

const getBlackOpaqueVariation = opacity => `rgba(0, 0, 0, ${opacity})`

const ButtonWrapper = styled.button`
  ${ButtonBase};
  ${getPadding};
  text-transform: ${getTextTransform};
  border: ${getBorderColor};
  background: ${getBackgroundColor};
  color: ${getTextColor};
  position: relative;

  @media (hover: hover) {
    &:hover {
      ${getHoverStyle}
    }
  }

  ${up('lg')} {
    &:hover {
      ${getHoverStyle}
    }
  }

  &:active {
    ${getActiveFocusStyle}
  }
  ${props =>
    (props.disabled || props.loading) &&
    css`
      box-shadow: none;
      pointer-events: none;
      ${getDisabledStyle}
    `}
`

const ButtonTitle = styled.span`
  opacity: ${props => (props.loading ? 0 : 1)};
`

const Button = props => {
  const { primary, secondary, tertiary, theme, disabled, loading, size } = props
  return (
    <ButtonWrapper
      {...props}
      primary={primary}
      secondary={secondary}
      tertiary={tertiary}
      theme={theme}
      disabled={disabled}
      loading={loading}
      size={size}
    >
      <ButtonTitle loading={loading}>{props.children}</ButtonTitle>
      {loading && (
        <DotLoaderWrapper data-testid="animated-loader">
          <DotLoader primary={!primary} />
        </DotLoaderWrapper>
      )}
    </ButtonWrapper>
  )
}

const DotLoaderWrapper = styled.span`
  position: absolute;
  display: flex;
  justify-content: center;
`
export default Button
